import { useState, useEffect, useRef } from 'react';
import { ProofState } from '../types/proof';
import { JsCoq } from 'jscoq';
import type { CoqManager } from 'jscoq';
import { executeDocument, JsCoqExecutionError, recoverEditorWorker } from '../utils/proofCompletion';
import { parseJsCoqState, ppToString, rawGoalsAt, sentenceMessages } from '../utils/jsCoqState';
import { guardJsCoqReadiness, type RocqRuntimeStatus } from '../utils/jsCoqReadiness';
export { parseJsCoqState, ppToString } from '../utils/jsCoqState';

export function useJsCoq(containerId: string, theme: 'light' | 'dark' = 'light') {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [runtimeStatus, setRuntimeStatus] = useState<RocqRuntimeStatus>({ phase: 'starting' });
  const runtimeCleanupRef = useRef<(() => void) | null>(null);
  const coqManagerRef = useRef<CoqManager | null>(null);
  const initRef = useRef(false);
  const initialCodeRef = useRef<string | null>(null);
  const themeRef = useRef<'light' | 'dark'>(theme);

  useEffect(() => () => { runtimeCleanupRef.current?.(); }, []);

  // Update theme when it changes
  useEffect(() => {
    themeRef.current = theme;
    const coqManager = coqManagerRef.current;
    if (coqManager?.provider && isLoaded) {
      // Map theme: 'light' -> 'default', 'dark' -> 'blackboard' for CodeMirror
      const editorTheme = theme === 'dark' ? 'blackboard' : 'default';
      coqManager.provider.configure({ theme: editorTheme });
    }
  }, [theme, isLoaded]);

  useEffect(() => {
    if (!containerId || initRef.current) return;
    initRef.current = true;
    loadJsCoq();
  }, [containerId]);

  useEffect(() => {
    const container = document.getElementById(containerId);
    const manager = coqManagerRef.current;
    if (!isLoaded || !container || !manager) return;

    let frame = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        manager.provider.snippets.forEach(snippet => snippet.editor.refresh());
      });
    });
    observer.observe(container);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [containerId, isLoaded]);

  const loadJsCoq = async () => {
    if (coqManagerRef.current) {
      setIsLoaded(true);
      return;
    }

    if (!containerId) {
      console.error('useJsCoq: containerId is undefined');
      return;
    }

    // Wait for container to be in DOM
    let container = document.getElementById(containerId);
    let retries = 0;
    while (!container && retries < 50) {
      await new Promise(resolve => setTimeout(resolve, 100));
      container = document.getElementById(containerId);
      retries++;
    }

    if (!container) {
      console.error(`Container ${containerId} not found`);
      setIsInitializing(false);
      setIsLoaded(false);
      setRuntimeStatus({ phase: 'error', message: 'The editor was not available during startup. Try starting Rocq again.' });
      return;
    }

    setIsInitializing(true);

    const wrapperId = containerId + '-wrapper';
    const basePath = new URL('/jscoq/', window.location.origin).href;

    try {
      let wrapper = document.getElementById(wrapperId);
      if (!wrapper) {
        wrapper = document.createElement('div');
        wrapper.id = wrapperId;
        wrapper.className = 'coq-wrapper';
        container.appendChild(wrapper);
      }

      // Map theme: 'light' -> 'light', 'dark' -> 'dark' (jsCoq uses these directly)
      const coqManager = await JsCoq.start([containerId], {
        wrapper_id: wrapperId,
        init_pkgs: ['coq'],
        all_pkgs: ['coq'],
        init_import: ['Coq.Lists.List'],

        theme: themeRef.current,
        base_path: basePath,
        pkg_path: basePath + 'coq-pkgs/',
        backend: 'wa',
        show: true,
        focus: true,
        prelude: true,
        implicit_libs: true,
      });

      coqManagerRef.current = coqManager;
      runtimeCleanupRef.current = guardJsCoqReadiness(coqManager, status => {
        setRuntimeStatus(status);
        setIsInitializing(status.phase === 'starting');
      });

      // If we have initial code queued, set it now using provider.load()
      if (initialCodeRef.current && coqManager.provider) {
        coqManager.provider.load(initialCodeRef.current);
        initialCodeRef.current = null;
      }

      setIsLoaded(true);
    } catch (error: any) {
      console.error('Failed to initialize jsCoq:', error);
      setIsInitializing(false);
      setIsLoaded(false);
      setRuntimeStatus({ phase: 'error', message: 'Could not load Rocq. Check your connection and try again. Your code is preserved.' });
    }
  };

  const getGoalsAndMessages = (verification: { isComplete: boolean; hasError: boolean; goals?: unknown }): ProofState => {
    if (!isLoaded || !coqManagerRef.current) {
      return {
        goals: [],
        hypotheses: [],
        messages: [],
        isComplete: false,
        hasError: false,
      };
    }

    try {
      const coqManager = coqManagerRef.current;
      const doc = coqManager.doc;

      if (!doc) {
        return {
          goals: [],
          hypotheses: [],
          messages: [],
          isComplete: false,
          hasError: false,
        };
      }

      const sentences = doc.sentences || [];
      const cachedGoals = rawGoalsAt(doc, coqManager.lastAdded()?.coq_sid ?? -1);
      const goals = cachedGoals === undefined ? verification.goals : cachedGoals;

      // Collect messages from doc.messages (Check/Compute outputs)
      const messages = sentenceMessages(coqManager.lastAdded(), coqManager);

      if (doc.messages && Array.isArray(doc.messages)) {
        doc.messages.forEach((msg: any) => {
          if (msg) {
            let messageText = '';
            let messageLevel: 'info' | 'warning' | 'error' = 'info';

            if (msg.msg) {
              messageText = ppToString(msg.msg, coqManager);
            } else if (msg.text) {
              messageText = typeof msg.text === 'string' ? msg.text : ppToString(msg.text, coqManager);
            } else if (typeof msg === 'string') {
              messageText = msg;
            } else if (msg.content) {
              messageText = ppToString(msg.content, coqManager);
            }

            if (msg.level) {
              messageLevel = msg.level;
            } else if (msg.feedback_id) {
              const fid = msg.feedback_id;
              if (Array.isArray(fid) && fid.length > 0) {
                const fidStr = fid[0]?.toString() || '';
                if (fidStr.includes('error') || fidStr.includes('Error')) messageLevel = 'error';
                else if (fidStr.includes('warning') || fidStr.includes('Warning')) messageLevel = 'warning';
              }
            }

            if (messageText && messageText.trim()) {
              messages.push({ level: messageLevel, text: messageText.trim() });
            }
          }
        });
      }

      // Collect error messages from sentences
      sentences.forEach((s: any) => {
        if ((['error', 'failed'].includes(String(s.phase).toLowerCase())) && s.feedback) {
          s.feedback.forEach((fb: any) => {
            if (fb?.msg) {
              const text = ppToString(fb.msg, coqManager);
              if (text?.trim() && !messages.some(m => m.text === text.trim())) {
                messages.push({ level: 'error', text: text.trim() });
              }
            }
          });
        }
      });

      const hasError = verification.hasError || sentences.some((s: any) => ['error', 'failed'].includes(String(s.phase).toLowerCase()));
      const parsedState = parseJsCoqState(goals, coqManager, messages);

      return {
        ...parsedState,
        isComplete: verification.isComplete && !hasError,
        hasError,
      };
    } catch (error: any) {
      return {
        goals: [],
        hypotheses: [],
        messages: [{
          level: 'error',
          text: error.message || 'Failed to get goals and messages',
        }],
        isComplete: false,
        hasError: true,
      };
    }
  };

  const executeProof = async (code: string): Promise<ProofState> => {
    if (!isLoaded || !coqManagerRef.current) {
      return {
        goals: [],
        hypotheses: [],
        messages: [],
        isComplete: false,
        hasError: false,
      };
    }

    try {
      const coqManager = coqManagerRef.current;
      const doc = coqManager.doc;

      if (!doc) {
        return {
          goals: [],
          hypotheses: [],
          messages: [],
          isComplete: false,
          hasError: false,
        };
      }

      const verification = await executeDocument(coqManager, code, getEditorValue);
      return getGoalsAndMessages(verification);
    } catch (error: any) {
      if (error instanceof JsCoqExecutionError && error.needsReset && coqManagerRef.current) {
        try {
          await recoverEditorWorker(coqManagerRef.current);
          return {
            goals: [], hypotheses: [], isComplete: false, hasError: true,
            messages: [{ level: 'warning', text: 'jsCoq encountered an internal error and was restarted. Your code is preserved. You can continue editing and check again.' }],
          };
        } catch {
          return {
            goals: [], hypotheses: [], isComplete: false, hasError: true,
            messages: [{ level: 'error', text: 'jsCoq could not restart automatically. Your code is still in the editor. Use Reset worker to retry.' }],
          };
        }
      }
      return {
        goals: [],
        hypotheses: [],
        messages: [{
          level: 'error',
          text: error.message || 'Proof execution failed',
        }],
        isComplete: false,
        hasError: true,
      };
    }
  };

  const getEditorValue = (): string => {
    const coqManager = coqManagerRef.current;
    if (!coqManager?.provider) return '';

    // Get from current focus or first snippet
    const provider = coqManager.provider.currentFocus || coqManager.provider.snippets[0];
    if (provider) {
      return provider.getText();
    }
    return '';
  };

  const setEditorValue = (value: string): boolean => {
    const coqManager = coqManagerRef.current;
    if (!coqManager?.provider) return false;

    try {
      // Use provider.load() to set the content
      coqManager.provider.load(value);
      if (coqManager.work) {
        coqManager.work();
      }
      return true;
    } catch (error) {
      console.error('Error setting editor value:', error);
      return false;
    }
  };

  const setInitialCode = (code: string) => {
    initialCodeRef.current = code;
    const coqManager = coqManagerRef.current;
    if (!coqManager?.provider) return;

    // Use provider.load() method to set the code
    coqManager.provider.load(code);
    initialCodeRef.current = null;
  };

  const retryInitialization = async () => {
    const manager = coqManagerRef.current;
    if (manager?.coq) {
      try { await manager.reset(); }
      catch (error) { console.error('Could not restart Rocq:', error); }
    } else {
      setRuntimeStatus({ phase: 'starting' });
      setIsInitializing(true);
      if (manager) {
        // The worker failed before launch; retain this editor and its text.
        try { await manager.launch(); }
        catch { setRuntimeStatus({ phase: 'error', message: 'Could not start Rocq. Your code is preserved.' }); setIsInitializing(false); }
      } else { await loadJsCoq(); }
    }
  };

  return {
    isLoaded,
    isInitializing,
    isReady: runtimeStatus.phase === 'ready',
    initializationError: runtimeStatus.phase === 'error' ? runtimeStatus.message : undefined,
    retryInitialization,
    executeProof,
    getEditorValue,
    setEditorValue,
    setInitialCode,
    coqManager: coqManagerRef.current,
  };
}
